import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { PenIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

const GET_OPERATING_PARTNERS = gql`
  query GetOperatingPartners($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        talentId {
          id
          operatingPartnerIds {
            id
            name
            position
            relevantExperience
          }
        }
      }
    }
  }
`;

const CREATE_OPERATING_PARTNER = gql`
  mutation CreateOperatingPartner(
    $talentId: ID!
    $input: OperatingPartnerInput!
  ) {
    createOperatingPartner(talentId: $talentId, input: $input) {
      ok
      operatingPartner {
        id
        name
        position
        relevantExperience
        talentId {
          id
          name
        }
      }
      errors
    }
  }
`;

const UPDATE_OPERATING_PARTNER = gql`
  mutation UpdateOperatingPartner($id: ID!, $input: OperatingPartnerInput!) {
    updateOperatingPartner(id: $id, input: $input) {
      ok
      operatingPartner {
        id
        name
        position
        relevantExperience
      }
      errors
    }
  }
`;

const DELETE_OPERATING_PARTNER = gql`
  mutation DeleteOperatingPartner($id: ID!) {
    deleteOperatingPartner(id: $id) {
      ok
      message
    }
  }
`;

interface CreateOperatingPartnerData {
  createOperatingPartner: {
    errors: string[];
    ok: boolean;
  };
}

interface DeleteOperatingPartnerData {
  deleteOperatingPartner: {
    message: null | string;
    ok: boolean;
  };
}

interface GetOperatingPartnersData {
  node: {
    talentId: {
      id: string;
      operatingPartnerIds: null | OperatingPartner[];
    } | null;
  } | null;
}

interface OperatingPartner {
  id: string;
  name: null | string;
  position: null | string;
  relevantExperience: null | string;
}

interface UpdateOperatingPartnerData {
  updateOperatingPartner: {
    errors: string[];
    ok: boolean;
  };
}

export function OperatingPartnersTable({
  opportunityId
}: {
  opportunityId: string;
}) {
  const { data } = useQuery<GetOperatingPartnersData>(GET_OPERATING_PARTNERS, {
    skip: !opportunityId,
    variables: { id: opportunityId }
  });

  const [createOperatingPartner, { loading: isCreatingOperatingPartner }] =
    useMutation<CreateOperatingPartnerData>(CREATE_OPERATING_PARTNER, {
      refetchQueries: [
        { query: GET_OPERATING_PARTNERS, variables: { id: opportunityId } }
      ]
    });

  const [updateOperatingPartner, { loading: isUpdatingOperatingPartner }] =
    useMutation<UpdateOperatingPartnerData>(UPDATE_OPERATING_PARTNER, {
      refetchQueries: [
        { query: GET_OPERATING_PARTNERS, variables: { id: opportunityId } }
      ]
    });

  const [deleteOperatingPartner, { loading: isDeletingOperatingPartner }] =
    useMutation<DeleteOperatingPartnerData>(DELETE_OPERATING_PARTNER, {
      refetchQueries: [
        { query: GET_OPERATING_PARTNERS, variables: { id: opportunityId } }
      ]
    });

  const talentId = data?.node?.talentId ?? null;
  const operatingPartners = talentId?.operatingPartnerIds ?? [];

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<null | OperatingPartner>(
    null
  );
  const [partnerToDelete, setPartnerToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!talentId) return;
    const formData = new FormData(event.currentTarget);
    const name = getString(formData, 'new-operating-partner-name') ?? '';
    const position =
      getString(formData, 'new-operating-partner-position') ?? '';
    const relevantExperience =
      getString(formData, 'new-operating-partner-relevant-experience') ?? '';
    try {
      const { data: result, error } = await createOperatingPartner({
        variables: {
          input: { name, position, relevantExperience },
          talentId: talentId.id
        }
      });
      if (error) throw new Error(error.message);
      const op = result?.createOperatingPartner;
      const errors = op?.errors;
      if (errors?.length) throw new Error(errors[0]);
      if (!op?.ok) throw new Error('An error has occurred');
      setAddDialogOpen(false);
      // event.currentTarget.reset();
      toast.success('Operating partner added');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to add');
    }
  };

  const handleUpdate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingPartner) return;
    const formData = new FormData(event.currentTarget);
    const name = getString(formData, 'edit-operating-partner-name') ?? '';
    const position =
      getString(formData, 'edit-operating-partner-position') ?? '';
    const relevantExperience =
      getString(formData, 'edit-operating-partner-relevant-experience') ?? '';
    try {
      const { data: result, error } = await updateOperatingPartner({
        variables: {
          id: editingPartner.id,
          input: { name, position, relevantExperience }
        }
      });
      if (error) throw new Error(error.message);
      const op = result?.updateOperatingPartner;
      const errors = op?.errors;
      if (errors?.length) throw new Error(errors[0]);
      if (!op?.ok) throw new Error('An error has occurred');
      setEditDialogOpen(false);
      setEditingPartner(null);
      toast.success('Operating partner updated');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const handleDelete = async (partnerId: string) => {
    try {
      const { data: result, error } = await deleteOperatingPartner({
        variables: { id: partnerId }
      });
      if (error) throw new Error(error.message);
      const op = result?.deleteOperatingPartner;
      if (!op?.ok) {
        throw new Error(op?.message ?? 'An error has occurred');
      }
      setPartnerToDelete(null);
      toast.success('Operating partner deleted');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  if (!talentId && data !== undefined) {
    return null;
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-end">
        <Dialog
          onOpenChange={open => {
            if (!open) setAddDialogOpen(false);
          }}
          open={addDialogOpen}
        >
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                setAddDialogOpen(true);
              }}
              type="button"
            >
              <PlusIcon />
              Add Operating Partner
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Add Operating Partner</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form
                id="add-operating-partner-form"
                noValidate
                onSubmit={event => void handleCreate(event)}
              >
                <FieldGroup>
                  <div className="5 grid grid-cols-2 gap-x-2">
                    <Field>
                      <FieldLabel htmlFor="new-operating-partner-name">
                        Name
                      </FieldLabel>
                      <Input
                        id="new-operating-partner-name"
                        name="new-operating-partner-name"
                        placeholder="Enter name"
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="new-operating-partner-position">
                        Position
                      </FieldLabel>
                      <Input
                        id="new-operating-partner-position"
                        name="new-operating-partner-position"
                        placeholder="Enter position"
                      />
                    </Field>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="new-operating-partner-relevant-experience">
                      Relevant experience
                    </FieldLabel>
                    <Input
                      id="new-operating-partner-relevant-experience"
                      name="new-operating-partner-relevant-experience"
                      placeholder="Enter relevant experience"
                    />
                  </Field>
                </FieldGroup>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isCreatingOperatingPartner}
                form="add-operating-partner-form"
              >
                {isCreatingOperatingPartner && <Spinner />}
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs font-semibold">Name</TableHead>
            <TableHead className="text-xs font-semibold">Position</TableHead>
            <TableHead className="text-xs font-semibold">
              Relevant experience
            </TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {operatingPartners.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={4}>
                <Empty className="px-0! pt-5! pb-0!">
                  <EmptyContent>No rows found</EmptyContent>
                </Empty>
              </TableCell>
            </TableRow>
          ) : (
            operatingPartners.map(partner => (
              <TableRow key={partner.id}>
                <TableCell className="font-medium">
                  {partner.name ?? '-'}
                </TableCell>
                <TableCell>{partner.position ?? '-'}</TableCell>
                <TableCell>{partner.relevantExperience ?? '-'}</TableCell>
                <TableCell className="p-1">
                  <div className="flex items-center gap-0.5">
                    <Button
                      onClick={() => {
                        setEditingPartner(partner);
                        setEditDialogOpen(true);
                      }}
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <PenIcon className="size-4" />
                    </Button>
                    <Button
                      onClick={() => {
                        setPartnerToDelete({
                          id: partner.id,
                          name: partner.name ?? 'this operating partner'
                        });
                      }}
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2Icon className="text-destructive size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <Dialog
        onOpenChange={open => {
          if (!open) {
            setEditDialogOpen(false);
            setEditingPartner(null);
          }
        }}
        open={editDialogOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Edit Operating Partner</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {editingPartner && (
              <form
                id="edit-operating-partner-form"
                noValidate
                onSubmit={event => void handleUpdate(event)}
              >
                <FieldGroup>
                  <div className="grid-x-2 5 grid grid-cols-2">
                    <Field>
                      <FieldLabel htmlFor="edit-operating-partner-name">
                        Name
                      </FieldLabel>
                      <Input
                        defaultValue={editingPartner.name ?? ''}
                        id="edit-operating-partner-name"
                        name="edit-operating-partner-name"
                        placeholder="Enter name"
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="edit-operating-partner-position">
                        Position
                      </FieldLabel>
                      <Input
                        defaultValue={editingPartner.position ?? ''}
                        id="edit-operating-partner-position"
                        name="edit-operating-partner-position"
                        placeholder="Enter position"
                      />
                    </Field>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="edit-operating-partner-relevant-experience">
                      Relevant experience
                    </FieldLabel>
                    <Input
                      defaultValue={editingPartner.relevantExperience ?? ''}
                      id="edit-operating-partner-relevant-experience"
                      name="edit-operating-partner-relevant-experience"
                      placeholder="Enter relevant experience"
                    />
                  </Field>
                </FieldGroup>
                <DialogFooter className="mt-4 mr-4 mb-4">
                  <Button
                    className="px-5 py-2.5"
                    disabled={isUpdatingOperatingPartner}
                    form="edit-operating-partner-form"
                    type="submit"
                  >
                    {isUpdatingOperatingPartner && <Spinner />}
                    Save
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
      <AlertDialog
        onOpenChange={open => {
          if (!open) setPartnerToDelete(null);
        }}
        open={partnerToDelete !== null}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete operating partner</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>
                {partnerToDelete?.name ?? 'this operating partner'}
              </strong>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeletingOperatingPartner}
              onClick={e => {
                e.preventDefault();
                if (partnerToDelete) {
                  void handleDelete(partnerToDelete.id);
                }
              }}
              variant="destructive"
            >
              {isDeletingOperatingPartner && <Spinner />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function getString(formData: FormData, key: string): null | string {
  const value = formData.get(key);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}
